import { jsx } from 'react/jsx-runtime';
import { useState, useEffect } from 'react';
import BaseSignUpView from './BaseSignUpView.js';
import { useGetRoleSignUpContentQuery } from '@sketch/gql-types';
import WorkspacesIcon from '@sketch/icons/workspaces-24.svg';
import ChatBubbleIcon from '@sketch/icons/chat-bubble-16.svg';
import InterfaceIcon from '@sketch/icons/interface-24.svg';
import { ImagesPreloader, LoadingState } from '@sketch/components';

const localRoleToGqlRole = {
    DESIGNER: 'designer',
    DESIGN_SYSTEM_MANAGER: 'designSystemManager',
    MANAGER_OR_DESIGN_TEAM_LEAD: 'managerOrDesignTeamLead',
    PRODUCT: 'product',
    DEVELOPER: 'developer',
    MARKETING_SPECIALIST: 'marketingSpecialist',
    WRITER: 'writer',
    FINANCE_OR_ADMIN: 'financeOrAdmin',
    RESEARCHER: 'researcher',
    STUDENT: 'student',
    TEACHER: 'teacher',
    EDUCATIONAL_INSTITUTION: 'educationalInstitution',
    OTHER: 'other',
};
const getIconsToPreload = (data) => {
    if (!data) {
        return [];
    }
    return Object.values(localRoleToGqlRole).flatMap(role => {
        const { features } = data.roleSignUpContent[role];
        return features.map(({ icon }) => {
            return {
                src: icon.src,
                srcset: [],
            };
        });
    });
};
const RoleSpecificSignUpView = props => {
    var _a;
    const initialRole = (_a = props['professionalRole']) !== null && _a !== void 0 ? _a : 'OTHER';
    const [role, setRole] = useState(initialRole);
    const { data, loading, error } = useGetRoleSignUpContentQuery();
    useEffect(() => {
        const iconsToPreload = getIconsToPreload(data);
        // pre-load the icons for each role to prevent a delay when the user
        // switches role
        if (iconsToPreload.length === 0) {
            return;
        }
        const resolveImageSrc = (s) => s;
        const preloader = new ImagesPreloader(iconsToPreload, resolveImageSrc);
        preloader.whenReady().catch(() => {
            // When the component unmounts the promise to preload the images will be
            // cancelled by `dispose()` below.
            // To avoid an "unhandled rejection error" from bubbling up, we add this
            // catch and do nothing
        });
        return () => {
            preloader.dispose();
        };
    }, [data]);
    if (loading) {
        return jsx(LoadingState, {});
    }
    if (error || !data) {
        // If an unexpected error occurs when trying to fetch the role content,
        // fall back to showing default content rather than an error page which
        // would prevent the user from signing up
        return jsx(BaseSignUpView, Object.assign({ content: defaultContent }, props));
    }
    const roleKey = localRoleToGqlRole[role];
    const content = data.roleSignUpContent[roleKey];
    return jsx(BaseSignUpView, Object.assign({ content: content, onRoleChange: setRole }, props));
};
const defaultContent = {
    __typename: 'SignUpContent',
    title: 'Sketch is your all-in-one design toolkit',
    tint: 'ORANGE',
    introduction: 'Sketch gives you all the tools you need for truly collaborative design. From early ideas to pixel-perfect artwork, playable prototypes and handoff, it all starts here.',
    features: [
        {
            __typename: 'SignUpFeature',
            description: 'Get access to our native Mac app and Workspaces',
            icon: {
                __typename: 'SignUpImage',
                src: InterfaceIcon,
                alt: 'interface icon',
            },
        },
        {
            __typename: 'SignUpFeature',
            description: 'Online Workspace to store and share your work',
            icon: {
                __typename: 'SignUpImage',
                src: WorkspacesIcon,
                alt: 'workspaces icon',
            },
        },
        {
            __typename: 'SignUpFeature',
            description: 'Powerful sharing and collaboration tools',
            icon: {
                __typename: 'SignUpImage',
                src: ChatBubbleIcon,
                alt: 'chat bubble icon',
            },
        },
    ],
    callToAction: 'Sign Up',
    relatedContent: null,
    heroImage: null,
};

export { RoleSpecificSignUpView as default };
